import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { Confirm16, MakeCopy16 } from 'ui/icons';
import {
  body2Bold,
  Caption,
  colors,
  duration,
  easing,
  largeShadow,
  monospace2,
  spacing,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useClickAway } from 'react-use';
import { addNegativeToast } from 'modules/toastContainer';
import { useShareUrl } from '../hooks';

const Error = styled.div`
  margin-bottom: ${spacing.half};
`;

const Item = styled.li`
  display: list-item;
  list-style-type: disc;
`;

const Text = styled.div`
  ${body2Bold}
  margin-bottom: ${spacing.half}
`;

const PopUp = styled.div`
  background: ${colors.white};
  border-radius: ${spacing.half};
  margin-left: -${pxToRem(296)};
  margin-top: ${spacing.half};
  padding: ${spacing.plusHalf};
  position: absolute;
  width: ${spacing.x24};
  z-index: 10;
  ${largeShadow};
`;

const UrlWrapper = styled.div`
  align-items: center;
  background: ${colors.backgroundTertiary};
  border-radius: ${spacing.quarter};
  border: ${spacing.single} solid ${colors.grey15};
  display: flex;
  margin-bottom: ${spacing.half};
  padding: ${spacing.quarter} ${spacing.quarter} ${spacing.quarter} 0;
  cursor: pointer;
  transition: all ${easing.exit} ${duration.short};

  &:hover {
    border-color: ${colors.borderInteractive};
    transition: all ${easing.entrance} ${duration.short};
  }
`;

const Url = styled.div`
  ${monospace2}
  color: ${colors.textSecondary};
  margin-left: ${spacing.threeQuarter};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const StyledCaption = styled(Caption)`
  color: ${colors.textSecondary};
`;

type ShareUrlProps = {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const copy = {
  negativeMsg: 'Could not copy url to clipboard. Please try again.',
  headerText: 'Share your selected filters and date range',
  subtext: 'Only users in your Iterable project can view this link.',
  copied: 'Copied!',
  toCopy: 'Copy',
  urlTooLong:
    'Too many filters are currently selected. The following were excluded from the share URL:',
};

export const ShareUrlPopUp = ({ setIsOpen }: ShareUrlProps) => {
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);
  const { completeUrl, error } = useShareUrl();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(completeUrl);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 3000);
    } catch (err) {
      setCopied(false);
      dispatch(addNegativeToast(copy.negativeMsg));
    }
  };

  // close on click outside
  const wrapperRef = useRef(null);
  useClickAway(wrapperRef, () => {
    setIsOpen(false);
  });

  // url max length error
  useEffect(() => {
    if (error.length > 0) {
      setIsOpen(false);
      dispatch(
        addNegativeToast(
          <>
            <Error>{copy.urlTooLong}</Error>
            <ul>
              {error.map((e, i) => (
                <Item key={i}>{e}</Item>
              ))}
            </ul>
          </>,
        ),
      );
    }
  }, []);

  return (
    <PopUp ref={wrapperRef}>
      <Text>{copy.headerText}</Text>
      <UrlWrapper onClick={handleCopy}>
        <Url>{completeUrl}</Url>
        <Button onClick={handleCopy} size={BUTTON_SIZES.SMALL} variant={BUTTON_VARIANTS.PRIMARY}>
          {copied ? <Confirm16 /> : <MakeCopy16 />}
          {copied ? copy.copied : copy.toCopy}
        </Button>
      </UrlWrapper>
      <StyledCaption>{copy.subtext}</StyledCaption>
    </PopUp>
  );
};
